// src/Stacks3D.js
import React, { useRef, useState, useEffect, createRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { MeshReflectorMaterial } from "@react-three/drei";

// Define available colors
const colors = ["red", "green", "blue", "yellow", "purple"];

// Utility to get a random color
function getRandomColor() {
  return colors[Math.floor(Math.random() * colors.length)];
}

// Generate a cluster of 1–5 objects with no overlap
function generateCluster(isBlockMode, boundsX, boundsZ) {
  const n = Math.floor(Math.random() * 5) + 1; // 1 to 5 objects
  const groupId = Math.random().toString(36).substring(2); // Unique group ID
  const positions = [new THREE.Vector3(0, 0, 0)]; // Start with one object at origin
  const size = isBlockMode ? 1 : 1; // halfSize for blocks, radius for balls

  for (let i = 1; i < n; i++) {
    let added = false;
    for (let tries = 0; tries < 20; tries++) {
      const randIndex = Math.floor(Math.random() * positions.length);
      const base = positions[randIndex];
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.random() * Math.PI;
      const dir = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta),
        Math.sin(phi) * Math.sin(theta),
        Math.cos(phi)
      );
      const newPos = base.clone().add(dir.multiplyScalar(2 * size * 1.1)); // Slightly more than size to avoid overlap

      // Ensure new position is within bounds
      if (
        Math.abs(newPos.x) > boundsX - size ||
        Math.abs(newPos.z) > boundsZ - size
      ) {
        continue;
      }

      const overlap = isBlockMode
        ? positions.some((p) => {
            const dx = Math.abs(p.x - newPos.x);
            const dy = Math.abs(p.y - newPos.y);
            const dz = Math.abs(p.z - newPos.z);
            return dx < 2 * size && dy < 2 * size && dz < 2 * size;
          })
        : positions.some((p) => p.distanceTo(newPos) < 2 * size * 0.95);

      if (!overlap) {
        positions.push(newPos);
        added = true;
        break;
      }
    }
    if (!added) break; // Stop if no valid position found
  }

  return positions.map((p) => ({
    relPos: p,
    color: getRandomColor(),
    id: Math.random().toString(36).substring(2), // Unique ID for React keys
    groupId,
  }));
}

// Component to set up the camera and renderer
function SceneSetup({ rotationAngle, mouseControl, boundsX, boundsZ }) {
  const { set, gl, camera } = useThree();

  useEffect(() => {
    gl.shadowMap.enabled = true;
    gl.shadowMap.type = THREE.PCFSoftShadowMap;

    const aspect = gl.domElement.clientWidth / gl.domElement.clientHeight;
    const fov = 75;
    const cam = new THREE.PerspectiveCamera(fov, aspect, 0.1, 1000);
    set({ camera: cam });

    const updateCamera = () => {
      const { clientWidth, clientHeight } = gl.domElement;
      cam.aspect = clientWidth / clientHeight;
      cam.updateProjectionMatrix();
      gl.setSize(clientWidth, clientHeight);
    };

    updateCamera(); // Initial call to set size
    window.addEventListener("resize", updateCamera);
    return () => window.removeEventListener("resize", updateCamera);
  }, [set, gl]);

  const previousMouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseDown = (e) => {
      mouseControl.current.isDragging = true;
      previousMouse.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseMove = (e) => {
      if (!mouseControl.current.isDragging) return;
      const deltaX = e.clientX - previousMouse.current.x;
      const deltaY = e.clientY - previousMouse.current.y;
      rotationAngle.current -= deltaX * 0.005;
      mouseControl.current.elevation = Math.max(
        5,
        Math.min(25, mouseControl.current.elevation - deltaY * 0.05)
      );
      previousMouse.current = { x: e.clientX, y: e.clientY };
    };
    const handleMouseUp = () => {
      mouseControl.current.isDragging = false;
    };
    window.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousedown", handleMouseDown);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [rotationAngle, mouseControl]);

  useFrame(() => {
    const radius = boundsX * 0.5; // Reduced to bring camera closer (was 0.75)
    const height = mouseControl.current.elevation;
    camera.position.set(
      radius * Math.cos(rotationAngle.current),
      height,
      radius * Math.sin(rotationAngle.current)
    );
    camera.lookAt(0, 0, 0);
  });

  return null;
}

function GameScene({
  setScore,
  setRemovedObjects,
  resetGameSignal,
  isBlockMode,
  boundsX,
  boundsZ,
}) {
  const fallingGroup = useRef();
  const [fallingObjects, setFallingObjects] = useState(
    generateCluster(isBlockMode, boundsX, boundsZ)
  );
  const [stacked, setStacked] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const keysRef = useRef({
    left: false,
    right: false,
    up: false,
    down: false,
    z: false,
    x: false,
    space: false,
  });
  const rotationAngle = useRef(0); // Track camera rotation angle
  const mouseControl = useRef({ elevation: 15, isDragging: false }); // Initial camera height

  const size = 1; // halfSize for blocks, radius for balls
  const speed = 0.02;
  const moveSpeed = 0.2;
  const maxHeight = 20;
  const rollSpeed = 0.01;
  const epsilon = 0.001;

  // Handle game reset
  useEffect(() => {
    const handleReset = () => {
      setStacked([]);
      setFallingObjects(generateCluster(isBlockMode, boundsX, boundsZ));
      setGameOver(false);
      rotationAngle.current = 0;
      mouseControl.current.elevation = 15;
      setRemovedObjects([]); // Reset removed objects
    };
    window.addEventListener("resetGame", handleReset);
    return () => window.removeEventListener("resetGame", handleReset);
  }, [resetGameSignal, setRemovedObjects, isBlockMode, boundsX, boundsZ]);

  // Reset falling group position and regenerate cluster when mode or bounds change
  useEffect(() => {
    setStacked([]);
    setFallingObjects(generateCluster(isBlockMode, boundsX, boundsZ));
    setGameOver(false);
    if (fallingGroup.current) {
      fallingGroup.current.position.set(0, 20, 0);
    }
  }, [isBlockMode, boundsX, boundsZ]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameOver) return;
      switch (e.key.toLowerCase()) {
        case "arrowleft":
          keysRef.current.left = true;
          break;
        case "arrowright":
          keysRef.current.right = true;
          break;
        case "arrowup":
          keysRef.current.up = true;
          break;
        case "arrowdown":
          keysRef.current.down = true;
          break;
        case "z":
          keysRef.current.z = true;
          break;
        case "x":
          keysRef.current.x = true;
          break;
        case " ":
          keysRef.current.space = true;
          e.preventDefault();
          break;
        default:
          break;
      }
    };

    const handleKeyUp = (e) => {
      switch (e.key.toLowerCase()) {
        case "arrowleft":
          keysRef.current.left = false;
          break;
        case "arrowright":
          keysRef.current.right = false;
          break;
        case "arrowup":
          keysRef.current.up = false;
          break;
        case "arrowdown":
          keysRef.current.down = false;
          break;
        case "z":
          keysRef.current.z = false;
          break;
        case "x":
          keysRef.current.x = false;
          break;
        case " ":
          keysRef.current.space = false;
          break;
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [gameOver]);

  // Reset falling group position
  useEffect(() => {
    if (fallingGroup.current && !gameOver) {
      fallingGroup.current.position.set(0, 20, 0);
    }
  }, [fallingObjects, gameOver]);

  // Find lowest valid Y position for the falling cluster
  const findLowestY = (groupPos, objects, stackedObjects) => {
    let minY = groupPos.y;
    objects.forEach((fo) => {
      const absPos = groupPos.clone().add(fo.relPos);
      let lowestY = size - fo.relPos.y; // Ground level
      stackedObjects.forEach((so) => {
        if (!so.removing && !so.falling) {
          if (isBlockMode) {
            const dx = Math.abs(absPos.x - so.pos.x);
            const dz = Math.abs(absPos.z - so.pos.z);
            if (dx <= 2 * size && dz <= 2 * size) {
              const possibleY = so.pos.y + 2 * size - fo.relPos.y; // Stack blocks directly
              if (possibleY > lowestY) {
                lowestY = possibleY;
              }
            }
          } else {
            const distanceXZ = Math.sqrt(
              (absPos.x - so.pos.x) ** 2 + (absPos.z - so.pos.z) ** 2
            );
            if (distanceXZ <= 2 * size) {
              const possibleY =
                so.pos.y +
                Math.sqrt((2 * size) ** 2 - distanceXZ ** 2) -
                fo.relPos.y;
              if (possibleY > lowestY) {
                lowestY = possibleY;
              }
            }
          }
        }
      });
      if (lowestY < minY) {
        minY = lowestY;
      }
    });
    return minY;
  };

  // Handle collision logic
  const handleCollision = (group, objects, stackedObjects, state) => {
    const newStacked = [...stackedObjects];
    const added = [];
    objects.forEach((fo) => {
      const absPos = group.position.clone().add(fo.relPos);
      const matRef = createRef();
      const newObject = {
        id: fo.id,
        pos: absPos,
        color: fo.color,
        groupId: fo.groupId,
        removing: false,
        removeTime: 0,
        matRef,
        toRemove: false,
        falling: false,
        rollVelocity: new THREE.Vector3(0, 0, 0),
      };
      newStacked.push(newObject);
      added.push(newObject);
    });

    // Check for matching colors
    const toRemove = new Set();
    added.forEach((ao) => {
      stackedObjects.forEach((so) => {
        if (!so.removing && !so.falling && ao.color === so.color) {
          if (isBlockMode) {
            const dx = Math.abs(ao.pos.x - so.pos.x);
            const dy = Math.abs(ao.pos.y - so.pos.y);
            const dz = Math.abs(ao.pos.z - so.pos.z);
            if (
              dx <= 2 * size + epsilon &&
              dy <= 2 * size + epsilon &&
              dz <= 2 * size + epsilon
            ) {
              toRemove.add(ao);
              toRemove.add(so);
            }
          } else {
            if (ao.pos.distanceTo(so.pos) <= 2 * size + epsilon) {
              toRemove.add(ao);
              toRemove.add(so);
            }
          }
        }
      });
    });

    if (toRemove.size > 0) {
      setScore((prev) => prev + toRemove.size);
      const removedColors = Array.from(toRemove).map((obj) => obj.color);
      setRemovedObjects((prev) => [...prev, ...removedColors]);
    }

    toRemove.forEach((obj) => {
      if (!obj.removing) {
        obj.removing = true;
        obj.removeTime = state.clock.getElapsedTime();
      }
    });

    const isGameOver = newStacked.some(
      (so) => !so.removing && !so.falling && so.pos.y >= maxHeight - size
    );
    if (isGameOver) {
      setGameOver(true);
      window.dispatchEvent(new Event("gameOver"));
    } else {
      setStacked(newStacked);
      setFallingObjects(generateCluster(isBlockMode, boundsX, boundsZ));
    }
  };

  // Check if an object is supported
  const isSupported = (obj, otherObjects) => {
    if (obj.pos.y <= size + epsilon) return true;
    return otherObjects.some((other) => {
      if (other === obj || other.removing || other.falling) return false;
      if (isBlockMode) {
        return (
          Math.abs(obj.pos.x - other.pos.x) <= 2 * size + epsilon &&
          Math.abs(obj.pos.z - other.pos.z) <= 2 * size + epsilon &&
          obj.pos.y - other.pos.y <= 2 * size + epsilon &&
          other.pos.y < obj.pos.y
        );
      } else {
        return (
          obj.pos.distanceTo(other.pos) <= 2 * size + epsilon &&
          other.pos.y < obj.pos.y
        );
      }
    });
  };

  // Game loop
  useFrame((state) => {
    if (!fallingGroup.current || gameOver) return;

    const group = fallingGroup.current;

    // Camera-relative movement
    const forward = new THREE.Vector3();
    state.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();
    const right = new THREE.Vector3();
    right.crossVectors(new THREE.Vector3(0, 1, 0), forward).normalize();

    let dx = 0;
    let dz = 0;
    if (keysRef.current.up) {
      dx += forward.x * moveSpeed;
      dz += forward.z * moveSpeed;
    }
    if (keysRef.current.down) {
      dx -= forward.x * moveSpeed;
      dz -= forward.z * moveSpeed;
    }
    if (keysRef.current.left) {
      dx += right.x * moveSpeed; // ArrowLeft moves right
      dz += right.z * moveSpeed;
    }
    if (keysRef.current.right) {
      dx -= right.x * moveSpeed; // ArrowRight moves left
      dz -= right.z * moveSpeed;
    }
    if (keysRef.current.z) {
      dx -= right.x * moveSpeed; // Z moves left
      dz -= right.z * moveSpeed;
    }
    if (keysRef.current.x) {
      dx += right.x * moveSpeed; // X moves right
      dz += right.z * moveSpeed;
    }

    const newX = group.position.x + dx;
    const newZ = group.position.z + dz;
    let canMove = true;
    fallingObjects.forEach((fo) => {
      const absX = newX + fo.relPos.x;
      const absZ = newZ + fo.relPos.z;
      const testPos = new THREE.Vector3(
        absX,
        group.position.y + fo.relPos.y,
        absZ
      );
      if (Math.abs(absX) > boundsX - size || Math.abs(absZ) > boundsZ - size) {
        canMove = false;
      }
      stacked.forEach((so) => {
        if (!so.removing && !so.falling) {
          if (isBlockMode) {
            const dx = Math.abs(testPos.x - so.pos.x);
            const dz = Math.abs(testPos.z - so.pos.z);
            const dy = Math.abs(testPos.y - so.pos.y);
            if (dx <= 2 * size && dz <= 2 * size && dy <= 2 * size) {
              canMove = false;
            }
          } else {
            if (testPos.distanceTo(so.pos) <= 2 * size) {
              canMove = false;
            }
          }
        }
      });
    });
    if (canMove) {
      group.position.x = newX;
      group.position.z = newZ;
    }

    // Handle instant drop or normal fall
    let shouldCollide = false;
    if (keysRef.current.space) {
      const lowestY = findLowestY(group.position, fallingObjects, stacked);
      group.position.y = lowestY;
      keysRef.current.space = false;
      shouldCollide = true;
    } else {
      group.position.y -= speed;
      fallingObjects.forEach((fo) => {
        const absPos = group.position.clone().add(fo.relPos);
        if (absPos.y - size <= 0) {
          shouldCollide = true;
        }
        stacked.forEach((so) => {
          if (!so.removing && !so.falling) {
            if (isBlockMode) {
              const dx = Math.abs(absPos.x - so.pos.x);
              const dz = Math.abs(absPos.z - so.pos.z);
              const dy = Math.abs(absPos.y - so.pos.y);
              if (dx <= 2 * size && dz <= 2 * size && dy <= 2 * size) {
                shouldCollide = true;
              }
            } else {
              if (absPos.distanceTo(so.pos) <= 2 * size) {
                shouldCollide = true;
              }
            }
          }
        });
      });
    }

    if (shouldCollide) {
      handleCollision(group, fallingObjects, stacked, state);
    }

    // Handle fading, falling, and rolling objects
    let needUpdate = false;
    const newStacked = [...stacked];
    newStacked.forEach((so) => {
      if (so.removing) {
        const time = state.clock.getElapsedTime() - so.removeTime;
        const duration = 1;
        let opacity = 1 - time / duration;
        if (opacity < 0) opacity = 0;
        if (so.matRef.current) {
          so.matRef.current.opacity = opacity;
        }
        if (time > duration) {
          so.toRemove = true;
          needUpdate = true;
        }
      }
    });

    if (needUpdate) {
      const remainingObjects = newStacked.filter((o) => !o.toRemove);
      remainingObjects.forEach((obj) => {
        if (obj.removing || obj.falling) return;
        if (!isSupported(obj, remainingObjects)) {
          obj.falling = true;
          obj.rollVelocity = new THREE.Vector3(0, 0, 0);
        }
      });
      setStacked(remainingObjects);
      needUpdate = false;
    }

    newStacked.forEach((so) => {
      if (so.falling) {
        let newPos = so.pos.clone();
        newPos.y -= speed;

        let stopped = newPos.y <= size;
        let contactObject = null;
        let contactDistance = Infinity;
        newStacked.forEach((other) => {
          if (
            other !== so &&
            !other.removing &&
            !other.falling &&
            newPos.y > other.pos.y
          ) {
            const dist = isBlockMode
              ? Math.max(
                  Math.abs(newPos.x - other.pos.x),
                  Math.abs(newPos.z - other.pos.z)
                )
              : newPos.distanceTo(other.pos);
            if (
              (isBlockMode && dist <= 2 * size + epsilon) ||
              (!isBlockMode && dist <= 2 * size)
            ) {
              if (dist < contactDistance) {
                contactDistance = dist;
                contactObject = other;
                stopped = true;
              }
            }
          }
        });

        if (stopped) {
          so.falling = false;
          if (contactObject) {
            newPos.y = isBlockMode
              ? contactObject.pos.y + 2 * size
              : contactObject.pos.y +
                Math.sqrt((2 * size) ** 2 - contactDistance ** 2);
          } else {
            newPos.y = size;
          }
          so.pos = newPos;
          so.rollVelocity.set(0, 0, 0);
          newStacked.forEach((other) => {
            if (
              other !== so &&
              so.groupId !== other.groupId &&
              !other.removing &&
              !other.falling &&
              so.color === other.color
            ) {
              if (isBlockMode) {
                const dx = Math.abs(so.pos.x - other.pos.x);
                const dy = Math.abs(so.pos.y - other.pos.y);
                const dz = Math.abs(so.pos.z - other.pos.z);
                if (
                  dx <= 2 * size + epsilon &&
                  dy <= 2 * size + epsilon &&
                  dz <= 2 * size + epsilon
                ) {
                  so.removing = true;
                  so.removeTime = state.clock.getElapsedTime();
                  other.removing = true;
                  other.removeTime = state.clock.getElapsedTime();
                  setScore((prev) => prev + 2);
                  setRemovedObjects((prev) => [...prev, so.color, other.color]);
                }
              } else {
                if (so.pos.distanceTo(other.pos) <= 2 * size + epsilon) {
                  so.removing = true;
                  so.removeTime = state.clock.getElapsedTime();
                  other.removing = true;
                  other.removeTime = state.clock.getElapsedTime();
                  setScore((prev) => prev + 2);
                  setRemovedObjects((prev) => [...prev, so.color, other.color]);
                }
              }
            }
          });
        } else {
          so.pos = newPos;
          so.rollVelocity.set(0, 0, 0);
        }
        needUpdate = true;
      } else if (!isBlockMode && so.rollVelocity.length() > 0) {
        let newPos = so.pos.clone();
        newPos.add(so.rollVelocity);
        if (
          Math.abs(newPos.x) > boundsX - size ||
          Math.abs(newPos.z) > boundsZ - size
        ) {
          so.rollVelocity.set(0, 0, 0);
        } else {
          let collision = false;
          let newContactObject = null;
          let newContactDistance = Infinity;
          newStacked.forEach((other) => {
            if (
              other !== so &&
              !other.removing &&
              !other.falling &&
              newPos.y > other.pos.y
            ) {
              const dist = newPos.distanceTo(other.pos);
              if (dist <= 2 * size) {
                if (dist < newContactDistance) {
                  newContactDistance = dist;
                  newContactObject = other;
                  collision = true;
                }
              }
            }
          });
          if (!collision && newPos.y > size) {
            so.falling = true;
            so.rollVelocity.set(0, 0, 0);
            needUpdate = true;
          } else if (collision) {
            const direction = newPos
              .clone()
              .sub(newContactObject.pos)
              .normalize();
            newPos = newContactObject.pos
              .clone()
              .add(direction.multiplyScalar(2 * size));
            so.pos = newPos;
            const normal = direction;
            const verticalComponent = normal.y;
            if (verticalComponent < 0.99) {
              const downhill = new THREE.Vector3(
                -normal.x,
                0,
                -normal.z
              ).normalize();
              const magnitude =
                rollSpeed * Math.sqrt(1 - verticalComponent ** 2);
              so.rollVelocity = downhill.multiplyScalar(magnitude);
            } else {
              so.rollVelocity.set(0, 0, 0);
            }
            needUpdate = true;
          } else {
            newPos.y = size;
            so.pos = newPos;
            so.rollVelocity.set(0, 0, 0);
            needUpdate = true;
          }
          newStacked.forEach((other) => {
            if (
              other !== so &&
              so.groupId !== other.groupId &&
              !other.removing &&
              !other.falling &&
              so.color === other.color &&
              so.pos.distanceTo(other.pos) <= 2 * size + epsilon
            ) {
              so.removing = true;
              so.removeTime = state.clock.getElapsedTime();
              other.removing = true;
              other.removeTime = state.clock.getElapsedTime();
              setScore((prev) => prev + 2);
              setRemovedObjects((prev) => [...prev, so.color, other.color]);
            }
          });
        }
        so.rollVelocity.multiplyScalar(0.95);
        if (so.rollVelocity.length() < 0.001) {
          so.rollVelocity.set(0, 0, 0);
        }
      }
      if (
        !so.falling &&
        !so.removing &&
        so.pos.y > size + epsilon &&
        !isSupported(so, newStacked)
      ) {
        so.falling = true;
        so.rollVelocity.set(0, 0, 0);
        needUpdate = true;
      }
    });

    if (needUpdate) {
      setStacked(newStacked);
    }

    if (
      newStacked.some(
        (so) => !so.removing && !so.falling && so.pos.y >= maxHeight - size
      )
    ) {
      setGameOver(true);
      window.dispatchEvent(new Event("gameOver"));
    }
  });

  return (
    <>
      <ambientLight intensity={0.4} color="#ffffff" />
      <hemisphereLight
        intensity={0.6}
        skyColor="#87ceeb"
        groundColor="#8b5a2b"
        position={[0, 20, 0]}
      />
      <directionalLight
        position={[5, 10, 5]}
        intensity={1.2}
        color="#ffffff"
        castShadow
        shadow-mapSize-width={4096}
        shadow-mapSize-height={4096}
        shadow-camera-near={0.1}
        shadow-camera-far={50}
        shadow-camera-left={-boundsX * 1.2}
        shadow-camera-right={boundsX * 1.2}
        shadow-camera-top={boundsZ * 1.2}
        shadow-camera-bottom={-boundsZ * 1.2}
        shadow-bias={-0.0001}
      />
      <group ref={fallingGroup}>
        {fallingObjects.map((fo) => (
          <mesh key={fo.id} position={fo.relPos} castShadow receiveShadow>
            {isBlockMode ? (
              <boxGeometry args={[2, 2, 2]} />
            ) : (
              <sphereGeometry args={[size, 32, 32]} />
            )}
            <meshStandardMaterial
              color={fo.color}
              roughness={0.3}
              metalness={0.2}
            />
          </mesh>
        ))}
      </group>
      {stacked.map((so) => (
        <mesh key={so.id} position={so.pos} castShadow receiveShadow>
          {isBlockMode ? (
            <boxGeometry args={[2, 2, 2]} />
          ) : (
            <sphereGeometry args={[size, 32, 32]} />
          )}
          <meshStandardMaterial
            ref={so.matRef}
            color={so.color}
            roughness={0.3}
            metalness={0.2}
            transparent
            opacity={1}
          />
        </mesh>
      ))}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -size, 0]}
        receiveShadow
      >
        <planeGeometry args={[boundsX * 2, boundsZ * 2]} />
        <MeshReflectorMaterial
          color="#cccccc"
          roughness={0.2}
          metalness={0}
          mirror={1}
          blur={[0, 0]}
          mixBlur={0}
          mixStrength={0.8}
          resolution={2048}
        />
      </mesh>
      <gridHelper
        args={[boundsX * 2, boundsZ * 2, "#ffffff", "#888888"]}
        position={[0, -size + 0.01, 0]}
      />
    </>
  );
}

function Stacks3D() {
  const [score, setScore] = useState(0);
  const [removedObjects, setRemovedObjects] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [resetGameSignal, setResetGameSignal] = useState(0);
  const [isBlockMode, setIsBlockMode] = useState(false); // false for balls, true for blocks
  const [boardSize, setBoardSize] = useState(50); // Half-width, so 100x75 default
  const [showHelp, setShowHelp] = useState(false); // Control help visibility
  const containerRef = useRef(null);

  const boundsX = boardSize; // Half-width
  const boundsZ = boardSize * (600 / 800); // Half-height, maintaining 4:3

  // Handle ? key for toggling help
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        setShowHelp((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const resetGame = () => {
    setScore(0);
    setRemovedObjects([]);
    setGameOver(false);
    setResetGameSignal((prev) => prev + 1);
    window.dispatchEvent(new Event("resetGame"));
  };

  const toggleMode = () => {
    setIsBlockMode((prev) => !prev);
    setResetGameSignal((prev) => prev + 1); // Trigger reset to regenerate objects
    window.dispatchEvent(new Event("resetGame"));
  };

  // Handle mouse wheel for board resizing
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      setBoardSize((prev) => {
        const newSize = prev + (e.deltaY > 0 ? -2 : 2);
        return Math.max(10, Math.min(100, newSize)); // Limit between 20x15 and 200x150
      });
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
    }

    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
      }
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        position: "relative",
      }}
    >
      <Canvas
        style={{
          width: "100%",
          height: "100%",
        }}
      >
        <SceneSetup
          rotationAngle={useRef(0)}
          mouseControl={useRef({ elevation: 15, isDragging: false })}
          boundsX={boundsX}
          boundsZ={boundsZ}
        />
        <GameScene
          setScore={setScore}
          setRemovedObjects={setRemovedObjects}
          resetGameSignal={resetGameSignal}
          isBlockMode={isBlockMode}
          boundsX={boundsX}
          boundsZ={boundsZ}
        />
      </Canvas>
      <ScoreDisplay
        score={score}
        removedObjects={removedObjects}
        isBlockMode={isBlockMode}
      />
      <HelpDisplay showHelp={showHelp} />
      <GameOverOverlay gameOver={gameOver} resetGame={resetGame} />
      <button
        style={{
          position: "absolute",
          top: "10px",
          right: "10px",
          padding: "10px 20px",
          fontSize: "18px",
          cursor: "pointer",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          color: "white",
          border: "none",
          borderRadius: "5px",
        }}
        onClick={toggleMode}
      >
        Switch to {isBlockMode ? "Balls" : "Blocks"}
      </button>
    </div>
  );
}

function ScoreDisplay({ score, removedObjects, isBlockMode }) {
  const colorCounts = removedObjects.reduce((acc, color) => {
    acc[color] = (acc[color] || 0) + 1;
    return acc;
  }, {});

  return (
    <div
      style={{
        position: "absolute",
        top: "10px",
        left: "10px",
        color: "white",
        fontSize: "24px",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "10px",
        borderRadius: "5px",
        maxWidth: "300px",
      }}
    >
      <div>Score: {score}</div>
      <div>Removed {isBlockMode ? "Blocks" : "Balls"}:</div>
      {Object.entries(colorCounts).length > 0 ? (
        <ul style={{ margin: 0, paddingLeft: "20px" }}>
          {Object.entries(colorCounts).map(([color, count]) => (
            <li key={color} style={{ color }}>
              {color.charAt(0).toUpperCase() + color.slice(1)}: {count}
            </li>
          ))}
        </ul>
      ) : (
        <div>None</div>
      )}
    </div>
  );
}

function HelpDisplay({ showHelp }) {
  return (
    <div
      style={{
        position: "absolute",
        bottom: "10px",
        left: "10px",
        right: "10px",
        color: "white",
        fontSize: "16px",
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        padding: "10px",
        borderRadius: "5px",
        display: showHelp ? "flex" : "none",
        justifyContent: "space-around",
        alignItems: "center",
      }}
    >
      <div>
        <strong>Controls:</strong>
        <ul style={{ margin: 0, paddingLeft: "20px" }}>
          <li>Arrow Up/Down: Move forward/backward</li>
          <li>Arrow Left: Move right</li>
          <li>Arrow Right: Move left</li>
          <li>Z: Move left</li>
          <li>X: Move right</li>
          <li>Space: Drop instantly</li>
          <li>Mouse Drag: Rotate/elevate view</li>
          <li>Mouse Wheel: Resize board</li>
          <li>? : Toggle help</li>
        </ul>
      </div>
    </div>
  );
}

function GameOverOverlay({ gameOver, resetGame }) {
  const [isGameOver, setIsGameOver] = useState(gameOver);

  useEffect(() => {
    const handleGameOver = () => setIsGameOver(true);
    const handleReset = () => setIsGameOver(false);
    window.addEventListener("gameOver", handleGameOver);
    window.addEventListener("resetGame", handleReset);
    return () => {
      window.removeEventListener("gameOver", handleGameOver);
      window.removeEventListener("resetGame", handleReset);
    };
  }, []);

  if (!isGameOver) return null;

  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        color: "white",
        padding: "20px",
        borderRadius: "10px",
        fontSize: "24px",
        textAlign: "center",
      }}
    >
      Game Over
      <br />
      <button
        style={{
          marginTop: "10px",
          padding: "10px 20px",
          fontSize: "18px",
          cursor: "pointer",
        }}
        onClick={resetGame}
      >
        Restart
      </button>
    </div>
  );
}

export default Stacks3D;
